import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import SnapshotsTable from '../components/SnapshotsTable';
import TreeView from '../components/TreeView';
import type { Archive } from '../types';

/**
 * Focused subtree view at /archives/:id/tree?prefix=/foo/bar/.
 *
 * Renders only the subtree rooted at `prefix`, with a breadcrumb back to the
 * archive page and intermediate ancestor prefixes (each ancestor links to its
 * own focused view). Default prefix is `/` if none given — equivalent to the
 * archive's tree tab but as a standalone page.
 */
export default function ArchiveTreePage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const [archive, setArchive] = useState<Archive | null>(null);

  const rawPrefix = params.get('prefix') || '/';
  // Defensive normalization — match TreeController's prefix rules: must start
  // with `/`, no `..` traversal, no `//` collapse. Bad URLs get the root tree.
  const prefix = rawPrefix.startsWith('/') && !rawPrefix.includes('..') && !rawPrefix.includes('//')
    ? rawPrefix
    : '/';

  useEffect(() => {
    if (!id) return;
    api<{ data: Archive }>(`/archives/${id}`).then(({ data }) => setArchive(data));
  }, [id]);

  if (!id) return <p className="muted">Missing archive id.</p>;
  if (!archive) return <p className="muted">Loading…</p>;

  // Trailing slash → directory mode (hierarchical tree).
  // No trailing slash → file mode: a single URL path's query-string variants.
  // We render the snapshot table directly there since there are no sub-dirs
  // and the user wants pagination + sort over potentially many variants.
  const isFileMode = !prefix.endsWith('/');

  return (
    <>
      <div className="card">
        <Breadcrumb archive={archive} prefix={prefix} />
      </div>

      <div className="card">
        {isFileMode
          ? <SnapshotsTable archiveId={archive.id} exactPath={prefix} />
          : <TreeView archiveId={archive.id} initialPrefix={prefix} />}
      </div>
    </>
  );
}

function Breadcrumb({ archive, prefix }: { archive: Archive; prefix: string }) {
  // Split `/foo/bar/` into ancestor prefixes: ['/', '/foo/', '/foo/bar/'].
  // Each non-final entry links to its own focused view; the final entry is
  // rendered as plain text (the current location).
  const segments = prefix === '/' ? [] : prefix.replace(/^\/|\/$/g, '').split('/');
  const ancestors: { label: string; target: string }[] = [{ label: '/', target: '/' }];
  let acc = '';
  for (const seg of segments) {
    acc += `/${seg}`;
    ancestors.push({ label: `${seg}/`, target: `${acc}/` });
  }

  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', fontSize: 14 }}>
      <Link to={`/archives/${archive.id}`}>← {archive.domain}</Link>
      <span className="muted">›</span>
      {ancestors.map((a, i) => {
        const isLast = i === ancestors.length - 1;
        return (
          <span key={a.target} style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            {isLast ? (
              <span style={{ fontFamily: 'monospace' }}>{a.label}</span>
            ) : (
              <Link
                to={`/archives/${archive.id}/tree?prefix=${encodeURIComponent(a.target)}`}
                style={{ fontFamily: 'monospace' }}
              >
                {a.label}
              </Link>
            )}
            {!isLast && <span className="muted">›</span>}
          </span>
        );
      })}
    </div>
  );
}
